import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { EmployeeRateRow, EmployeeRateWarning, PayrollEmployee } from "@/api/types";
import { formatMoney } from "@/lib/format";
import { DialogShell } from "./DialogShell";
import "./EmployeeFormDialog.css";
import "./RateHistoryDialog.css";

/**
 * ef-058-4 — a person's dated pay-rate history (GET /api/demo/employees/{id}/rates, newest
 * effective date first) plus a form that adds a row (POST .../rates).
 *
 * A rate is history, never an edit: changing it appends a dated row and the old rows stay.
 * The server may answer 201 WITH a warning (decision D2: a back-dated change under a finalized
 * pay run is allowed, flagged, and never rewrites the run) — that text is shown, not swallowed.
 *
 * ONE `role="alert"` region carries both errors and warnings so a screen reader hears a single
 * announcement. Same shell as W4Dialog: mounts per open, focus trap, Escape/overlay close.
 */
export function RateHistoryDialog({
  employee,
  onClose,
  onChanged,
}: {
  employee: PayrollEmployee;
  onClose: () => void;
  /** Called after each saved change — the caller refetches the roster so its Rate column
   *  reads the rate now in effect. */
  onChanged: () => void;
}) {
  const [rates, setRates] = useState<EmployeeRateRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [payBasis, setPayBasis] = useState<"hourly" | "salary">(employee.payBasis === "salary" ? "salary" : "hourly");
  const [rate, setRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const [warnings, setWarnings] = useState<EmployeeRateWarning[]>([]);
  const [saved, setSaved] = useState(false);

  const alertId = useId();
  const payBasisId = useId();
  const rateId = useId();
  const effectiveId = useId();
  const formId = useId();
  const inFlight = useRef(false);

  const load = useCallback(
    (signal?: AbortSignal) =>
      demoApi
        .getEmployeeRates(employee.id, signal)
        .then((rows) => {
          setRates(rows);
          setLoadError(null);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setLoadError(problemMessage(err, "Could not load this rate history."));
        }),
    [employee.id],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;
    setError(null);
    setInvalid(new Set());
    setWarnings([]);
    setSaved(false);

    if (effectiveFrom === "") {
      setError("Effective from: choose the date this rate starts.");
      setInvalid(new Set(["effectivefrom"]));
      return;
    }

    inFlight.current = true;
    setSaving(true);
    try {
      const result = await demoApi.changeEmployeeRate(employee.id, {
        payBasis,
        rate: rate.trim() === "" ? null : Number(rate),
        effectiveFrom,
      });
      setWarnings(result.warnings ?? []);
      setSaved(true);
      setRate("");
      setEffectiveFrom("");
      onChanged();
      await load();
    } catch (err) {
      const fields = problemFieldErrors(err);
      const entries = Object.entries(fields ?? {}).filter(([, msgs]) => msgs.length > 0);
      if (entries.length > 0) {
        setInvalid(new Set(entries.map(([key]) => key.toLowerCase())));
        setError(entries.map(([key, msgs]) => `${key}: ${msgs[0] ?? ""}`).join(" "));
      } else {
        setError(problemMessage(err, "Could not change this rate."));
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const hasAlert = error !== null || loadError !== null || warnings.length > 0;
  const state = loadError ? "error" : rates === null ? "loading" : rates.length === 0 ? "empty" : "ready";

  return (
    <DialogShell
      title={<>Rate history — {employee.name}</>}
      onClose={onClose}
      busy={saving}
      dataState={state}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Close
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Change rate"}
          </button>
        </>
      }
    >
      <div className="rate-history__section">
        {rates === null && !loadError && <p className="emp-dialog__hint">Loading rate history…</p>}
        {rates !== null && rates.length === 0 && <p className="emp-dialog__hint">No rates on file yet.</p>}
        {rates !== null && rates.length > 0 && (
          <div className="rate-history__scroll">
            <table className="emp-dialog__table ruled-rows" aria-label="Rate history">
              <thead>
                <tr>
                  <th scope="col">Effective from</th>
                  <th scope="col">Pay basis</th>
                  <th scope="col" className="num">
                    Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {rates.map((row) => (
                  <tr key={row.id}>
                    <td className="num">{row.effectiveFrom}</td>
                    <td>{row.payBasis === "salary" ? "Salary" : "Hourly"}</td>
                    <td className="num">{row.rate !== null ? formatMoney(row.rate) : "Not set"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <h3 className="rate-history__heading">Change the rate</h3>

        <div className="emp-dialog__field">
          <label htmlFor={payBasisId}>Pay basis</label>
          <select
            id={payBasisId}
            value={payBasis}
            onChange={(e) => setPayBasis(e.target.value === "salary" ? "salary" : "hourly")}
            aria-invalid={invalid.has("paybasis") ? true : undefined}
          >
            <option value="hourly">Hourly</option>
            <option value="salary">Salary</option>
          </select>
        </div>

        <div className="emp-dialog__field">
          <label htmlFor={rateId}>New rate</label>
          <input
            id={rateId}
            type="number"
            step="0.01"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            aria-invalid={invalid.has("rate") ? true : undefined}
            aria-describedby={invalid.has("rate") ? alertId : undefined}
          />
        </div>

        <div className="emp-dialog__field">
          <label htmlFor={effectiveId}>Effective from</label>
          <input
            id={effectiveId}
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            aria-invalid={invalid.has("effectivefrom") ? true : undefined}
            aria-describedby={invalid.has("effectivefrom") ? alertId : undefined}
          />
        </div>

        {hasAlert && (
          <div id={alertId} className="rate-history__messages" role="alert">
            {loadError && <p className="emp-dialog__error">{loadError}</p>}
            {error && <p className="emp-dialog__error">{error}</p>}
            {warnings.map((w, i) => (
              <p key={`${w.code}-${i}`} className="emp-dialog__warning">
                {w.message}
              </p>
            ))}
          </div>
        )}
        {saved && warnings.length === 0 && (
          <p className="emp-dialog__hint" role="status">
            Rate changed.
          </p>
        )}

      </form>
    </DialogShell>
  );
}
